/**
 * Loan Purpose framework.
 * A two-step selector (category → specific purpose) with "Other" descriptions.
 * Persisted in scenario blob as PurposeSelection.
 */

export interface PurposeOption {
  id: string;
  label: string;
  isOther?: boolean;
}

export interface PurposeCategory {
  id: string;
  label: string;
  options: PurposeOption[];
  /** Optional context note rendered under the selector when this category is chosen. */
  note?: string;
}

export const PURPOSE_CATEGORIES: PurposeCategory[] = [
  {
    id: 'transportation',
    label: 'Transportation',
    options: [
      { id: 'vehicle_purchase', label: 'Vehicle purchase' },
      { id: 'vehicle_repair', label: 'Vehicle repair' },
      { id: 'rv_purchase', label: 'RV purchase' },
      { id: 'boat_purchase', label: 'Boat purchase' },
      { id: 'motorcycle_purchase', label: 'Motorcycle purchase' },
      { id: 'equipment_trailer', label: 'Equipment trailer' },
      { id: 'other_transport', label: 'Other transportation purpose', isOther: true },
    ],
  },
  {
    id: 'business',
    label: 'Business',
    options: [
      { id: 'business_capital', label: 'Business capital' },
      { id: 'business_funding', label: 'Business funding / loan' },
      { id: 'working_capital', label: 'Working capital' },
      { id: 'business_equipment', label: 'Business equipment' },
      { id: 'inventory', label: 'Inventory' },
      { id: 'marketing_growth', label: 'Marketing / growth' },
      { id: 'staffing', label: 'Staffing' },
      { id: 'business_acquisition', label: 'Business acquisition' },
      { id: 'commercial_real_estate', label: 'Commercial real estate' },
      { id: 'other_business', label: 'Other business purpose', isOther: true },
    ],
  },
  {
    id: 'real_estate',
    label: 'Real Estate',
    options: [
      { id: 'real_estate_purchase', label: 'Real estate purchase' },
      { id: 'rental_property', label: 'Rental property' },
      { id: 'down_payment', label: 'Down payment' },
      { id: 'renovation', label: 'Renovation' },
      { id: 'property_improvement', label: 'Property improvement' },
      { id: 'vacation_property', label: 'Vacation property' },
      { id: 'mortgage_recapture', label: 'Mortgage recapture' },
      { id: 'other_real_estate', label: 'Other real estate purpose', isOther: true },
    ],
  },
  {
    id: 'family_lifestyle',
    label: 'Family & Lifestyle',
    options: [
      { id: 'vacation', label: 'Vacation' },
      { id: 'wedding', label: 'Wedding' },
      { id: 'family_event', label: 'Family celebration / event' },
      { id: 'medical', label: 'Medical expense' },
      { id: 'emergency', label: 'Emergency expense' },
      { id: 'major_household', label: 'Major household purchase' },
      { id: 'other_family', label: 'Other family / lifestyle purpose', isOther: true },
    ],
  },
  {
    id: 'education',
    label: 'Education & Personal Development',
    options: [
      { id: 'education_college', label: 'Education (college / university)' },
      { id: 'education_personal', label: 'Education (personal development)' },
      { id: 'professional_dev', label: 'Professional development' },
      { id: 'coaching_training', label: 'Coaching / training' },
      { id: 'certification', label: 'Certification program' },
      { id: 'other_education', label: 'Other education / personal development purpose', isOther: true },
    ],
  },
  {
    id: 'debt_recapture',
    label: 'Debt Elimination / Recapture',
    note: 'Once this third-party debt is eliminated, consider redirecting the former payment amount toward your policy loan repayment or broader family financing strategy. This helps keep financial energy from being absorbed into lifestyle spending.',
    options: [
      { id: 'credit_card', label: 'Credit card debt' },
      { id: 'line_of_credit', label: 'Line of credit' },
      { id: 'vehicle_loan', label: 'Vehicle loan' },
      { id: 'bank_loan', label: 'Bank loan' },
      { id: 'mortgage', label: 'Mortgage' },
      { id: 'business_debt', label: 'Business debt' },
      { id: 'debt_consolidation', label: 'Debt consolidation' },
      { id: 'other_debt', label: 'Other debt recapture purpose', isOther: true },
    ],
  },
  {
    id: 'investment',
    label: 'Investment',
    options: [
      { id: 'registered_accounts', label: 'Registered accounts' },
      { id: 'stocks', label: 'Stocks' },
      { id: 'bonds', label: 'Bonds' },
      { id: 'mutual_fund', label: 'Mutual fund' },
      { id: 'segregated_fund', label: 'Segregated fund' },
      { id: 'private_lending', label: 'Private lending' },
      { id: 'rental_property_inv', label: 'Rental property' },
      { id: 'private_business', label: 'Private business investment' },
      { id: 'crypto', label: 'Crypto' },
      { id: 'precious_metals', label: 'Precious metals' },
      { id: 'other_investment', label: 'Other investment purpose', isOther: true },
    ],
  },
  {
    id: 'family_financing',
    label: 'Family Financing / Policy Funding',
    options: [
      { id: 'policy_premium', label: 'Policy premium' },
      { id: 'paid_up_additions', label: 'Paid-up additions' },
      { id: 'existing_policy_loan', label: 'Existing policy loan consolidation' },
      { id: 'family_financing_expansion', label: 'Family financing expansion' },
      { id: 'future_capital_reserve', label: 'Future family capital reserve' },
      { id: 'other_family_financing', label: 'Other family financing purpose', isOther: true },
    ],
  },
  {
    id: 'global_other',
    label: 'Global Other',
    options: [
      { id: 'other', label: 'Other', isOther: true },
    ],
  },
];

export interface PurposeSelection {
  categoryId: string | null;
  specificId: string | null;
  otherDescription: string;
}

export function emptyPurpose(): PurposeSelection {
  return { categoryId: null, specificId: null, otherDescription: '' };
}

export function findCategory(id: string | null): PurposeCategory | null {
  if (!id) return null;
  return PURPOSE_CATEGORIES.find((c) => c.id === id) ?? null;
}

export function findOption(categoryId: string | null, optionId: string | null): PurposeOption | null {
  const cat = findCategory(categoryId);
  if (!cat || !optionId) return null;
  return cat.options.find((o) => o.id === optionId) ?? null;
}

/** True when both category and specific purpose are selected. */
export function isPurposeSelected(p: PurposeSelection | null | undefined): boolean {
  if (!p) return false;
  return Boolean(p.categoryId && p.specificId);
}

/** True when an "Other" option is selected but the description is missing. */
export function purposeNeedsOtherDescription(p: PurposeSelection | null | undefined): boolean {
  if (!p) return false;
  const opt = findOption(p.categoryId, p.specificId);
  if (!opt?.isOther) return false;
  return p.otherDescription.trim().length === 0;
}

/** Human-readable summary for reports and lists. */
export function formatPurpose(p: PurposeSelection | null | undefined): string {
  if (!p || !p.categoryId) return '';
  const cat = findCategory(p.categoryId);
  const opt = findOption(p.categoryId, p.specificId);
  if (!cat) return '';
  if (!opt) return cat.label;
  if (opt.isOther && p.otherDescription.trim().length > 0) {
    return `${cat.label} — ${opt.label}: ${p.otherDescription.trim()}`;
  }
  return `${cat.label} — ${opt.label}`;
}